/*
 * cfg-cart-lock.js
 * Browserlaag van het groepsslot voor Horizon (cornhole.nl): houdt de verplichte
 * zakjes vast aan hun set. Alle beslissingen komen uit CfgCartGroup
 * (assets/cfg-cart-group.js); dit bestand doet alleen DOM en fetch.
 *
 * Twee verantwoordelijkheden:
 *   1. Klikken op de verwijderknop en de steppers van een SET vertalen naar één
 *      update over de hele groep.
 *   2. Bewaken: na elke cart-wijziging controleren of elke groep nog compleet is.
 *
 * Waarom allebei: (1) is het normale pad, (2) vangt de rest af — een volledige
 * navigatie naar /cart, een rechtstreeks aangeroepen /cart/change?line=2, en
 * winkelwagens die al openstonden.
 *
 * Horizon-specifiek: de drawer en de /cart-pagina renderen allebei
 * snippets/cart-products.liquid, dus één laag bedient beide. Geen enkel
 * themabestand wordt aangeraakt.
 */
(function () {
  'use strict';

  var G = window.CfgCartGroup;
  if (!G) return;

  // Eén vlag voor beide paden. De bewaker reageert op cart-wijzigingen en veroorzaakt
  // er zelf; zonder deze vlag zou hij op zijn eigen updates reageren.
  var bezig = false;

  /*
   * fetch verwerpt alleen bij netwerkfouten, niet bij een 4xx/5xx. Zonder deze
   * controle zou een foutbody ({ status, description }) gewoon als cart worden
   * doorgegeven en zou de winkelwagen er stilletjes naast zitten. Hier gooien we
   * wél, zodat de fout in de .catch-blokken beneden landt en zichtbaar wordt.
   */
  function alsJson(r) {
    return r.text().then(function (tekst) {
      var body;
      try {
        body = JSON.parse(tekst);
      } catch (e) {
        body = {};
      }
      if (!r.ok) throw new Error('[' + r.status + '] ' + (body.description || body.message || r.statusText || 'HTTP-fout'));
      return body;
    });
  }

  function haalCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(alsJson);
  }

  function verversThema(cart) {
    // Horizon: elke <cart-items-component> rendert zichzelf opnieuw op een
    // cart:update zonder sections-payload; de cart-bubble leest itemCount.
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: { resource: cart, sourceId: 'cfg-cart-lock', data: { itemCount: cart.item_count, source: 'cfg-cart-lock' } }
    }));
  }

  /*
   * Op Focal hingen hier de events waarmee LineItemQuantity zijn spinner toont.
   * Horizon kent die events niet: daar is de bezig-stand puur een klasse op de rij
   * (.is-cfg-bezig, zie cfg-cart-lock.css). Deze twee functies zijn de enige plek
   * die die klasse zet en weghaalt, zodat de rij precies zo lang grijs is als er
   * werkelijk een verzoek loopt — en altijd weer bijkleurt, ook na een fout.
   * Een toekomstige laadindicator hoort hier ook.
   */
  function startLader(regelEl) {
    if (!regelEl) return;
    regelEl.classList.add('is-cfg-bezig');
  }

  function stopLader(regelEl) {
    if (!regelEl) return;
    regelEl.classList.remove('is-cfg-bezig');
  }

  function stuurUpdates(updates) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ updates: updates })
    }).then(alsJson);
  }

  function stuurToevoegingen(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: items })
    }).then(alsJson);
  }

  /* ---------- 1. Groepsacties op de setregel ---------- */

  /*
   * Ververst ALTIJD aan het einde, ook als er niets te herstellen viel. Dat is het
   * verschil met de bewaker: die grijpt alleen in bij een afwijking, maar een
   * geslaagde groepsactie is juist het normale geval en moet gewoon zichtbaar worden.
   */
  function pasGroepAan(uid, nieuwAantal, regelEl) {
    // Niet onze beurt (de bewaker loopt nog): niets doen, en vooral geen laadstand
    // zetten die dan blijft hangen.
    if (bezig) return Promise.resolve();
    bezig = true;
    startLader(regelEl);
    return haalCart()
      .then(function (cart) {
        var updates = G.bepaalGroepsUpdate(cart, uid, nieuwAantal);
        // Niets te wijzigen: geef de zojuist opgehaalde cart door, dan hoeft
        // herstelStil hem niet nog een keer te halen.
        if (!Object.keys(updates).length) return cart;
        return stuurUpdates(updates);
      })
      .then(herstelStil)
      .then(haalCart)
      .then(function (cart) {
        bezig = false;
        stopLader(regelEl);
        verversThema(cart);
      })
      .catch(function (err) {
        bezig = false;
        stopLader(regelEl);
        console.warn('[cfg-lock] groepsactie mislukt:', err);
        // Toch herrenderen, anders blijft de winkelwagen een stand tonen die niet
        // meer klopt met wat er op de server staat.
        return haalCart().then(function (cart) { verversThema(cart); }).catch(function () {});
      });
  }

  function rolVan(el) {
    var regel = el.closest && el.closest('[data-cfg-uid][data-cfg-role="main"]');
    return regel || null;
  }

  // Horizon rendert het aantalveld in de winkelwagen als
  // <cart-quantity-selector-component>; input[name="updates[]"] is de vangnet-vorm
  // die ook geldt als het thema het veld ooit zonder die wrapper plaatst.
  var INPUT_SELECTOR = 'cart-quantity-selector-component input, input[name="updates[]"]';

  function huidigAantal(regel) {
    var input = regel.querySelector(INPUT_SELECTOR);
    var v = input ? parseInt(input.value, 10) : NaN;
    return isNaN(v) ? (parseInt(regel.getAttribute('data-cfg-qty'), 10) || 1) : v;
  }

  /*
   * Capture-fase op window, niet op document: Horizon registreert zijn eigen
   * on:<event>-afhandeling óók als capture-listener op document
   * (assets/component.js, registerEventListeners → { capture: true }). Twee
   * capture-listeners op hetzelfde knooppunt lopen in registratievolgorde, en die
   * volgorde is niet van ons — dit bestand staat later in de <head> dan de
   * modulescripts uit snippets/scripts.liquid. window ligt één stap hóger in het
   * capture-pad dan document, dus een stopPropagation() hier zorgt dat het event
   * document nooit bereikt en het thema de actie niet zelf ook nog uitvoert.
   */
  window.addEventListener('click', function (event) {
    var el = event.target.closest && event.target.closest('button');
    if (!el) return;
    var regel = rolVan(el);
    if (!regel) return;

    var uid = regel.getAttribute('data-cfg-uid');
    var huidig = huidigAantal(regel);
    var nieuw = null;
    var actie = el.getAttribute('on:click') || '';

    if (el.classList.contains('cart-items__remove') || actie.indexOf('/onLineItemRemove') === 0) {
      nieuw = 0;
    } else if (actie.indexOf('/increaseQuantity') === 0) {
      nieuw = huidig + 1;
    } else if (actie.indexOf('/decreaseQuantity') === 0) {
      nieuw = Math.max(0, huidig - 1);
    } else {
      return;
    }

    // Ook als we niets kunnen doen moet het thema erafblijven: zou het event
    // doorlopen, dan werkte Horizon één regel van de groep los bij.
    event.preventDefault();
    event.stopPropagation();
    if (nieuw === huidig) return;
    if (bezig) {
      console.warn('[cfg-lock] bezig, klik genegeerd');
      return;
    }
    pasGroepAan(uid, nieuw, regel);
  }, true);

  window.addEventListener('change', function (event) {
    var input = event.target;
    if (!input.matches || !input.matches(INPUT_SELECTOR)) return;
    var regel = rolVan(input);
    if (!regel) return;
    // Zelfde afweging als bij de klik: het thema mag deze wijziging nooit zelf
    // uitvoeren, ook niet wanneer wij hem laten vallen. change bubbelt (in
    // tegenstelling tot blur hieronder), dus stopPropagation() alleen is hier
    // voldoende — er is geen standaardactie om te onderdrukken.
    event.stopPropagation();
    if (bezig) {
      console.warn('[cfg-lock] bezig, wijziging genegeerd');
      input.value = parseInt(regel.getAttribute('data-cfg-qty'), 10) || 1;
      return;
    }
    pasGroepAan(regel.getAttribute('data-cfg-uid'), parseInt(input.value, 10), regel);
  }, true);

  /*
   * Horizon commit een handmatig getypt aantal niet op change maar op blur
   * (on:blur="/setQuantity" in snippets/quantity-selector.liquid). Die blur volgt ná
   * onze change-afhandeling en zou de setregel alsnog los bijwerken. Blur bubbelt
   * niet, maar de capture-fase loopt wel langs window, dus dezelfde truc werkt.
   */
  window.addEventListener('blur', function (event) {
    var input = event.target;
    if (!input || !input.matches || !input.matches(INPUT_SELECTOR)) return;
    if (!rolVan(input)) return;
    event.stopPropagation();
  }, true);

  /* ---------- 2. De bewaker ---------- */

  /*
   * Het herstelwerk zelf, zonder herrender en zonder de bezig-vlag: zo kan
   * pasGroepAan hem meenemen in zijn eigen keten en één keer aan het eind
   * verversen. Geeft terug of er daadwerkelijk iets is gewijzigd.
   *
   * De cart mag worden meegegeven — pasGroepAan heeft er net een verse (het
   * antwoord van /cart/update.js is zelf de nieuwe cart), dus dan hoeft hier geen
   * tweede /cart.js overheen. Zonder argument haalt hij hem zelf op; dat is het
   * pad van de bewaker.
   */
  function herstelStil(cart) {
    return (cart ? Promise.resolve(cart) : haalCart()).then(function (cart) {
      var plan = G.bepaalHerstel(cart);
      if (!plan.toevoegen.length && !Object.keys(plan.bijwerken).length) return false;

      var keten = Promise.resolve();
      // Eerst bijwerken, dan toevoegen: een update werkt op line keys, en die
      // schuiven niet op. Een toevoeging kan wel een nieuwe regel opleveren.
      if (Object.keys(plan.bijwerken).length) {
        keten = keten.then(function () { return stuurUpdates(plan.bijwerken); });
      }
      if (plan.toevoegen.length) {
        keten = keten.then(function () { return stuurToevoegingen(plan.toevoegen); });
      }
      return keten.then(function () { return true; });
    });
  }

  function bewaak() {
    if (bezig) return Promise.resolve();
    bezig = true;
    return herstelStil()
      .then(function (gewijzigd) {
        if (!gewijzigd) return null;
        return haalCart().then(function (cart) { verversThema(cart); });
      })
      .then(function () { bezig = false; })
      .catch(function (err) {
        bezig = false;
        console.warn('[cfg-lock] herstel mislukt:', err);
        // Toch herrenderen: een halve reparatie (update gelukt, toevoegen niet) heeft de cart al veranderd.
        return haalCart().then(function (cart) { verversThema(cart); }).catch(function () {});
      });
  }

  document.addEventListener('cart:update', function (event) {
    var bron = event.detail && event.detail.data && event.detail.data.source;
    if (bron === 'cfg-cart-lock') return; // eigen ververs-event, anders een lus
    bewaak();
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { bewaak(); });
  } else {
    bewaak();
  }
})();
